import {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
} from "react";

interface OtpBoxesProps {
  value: string;
  onChange: (next: string) => void;
  length: number;
  error?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
}

/**
 * Six (or N) individual single-digit inputs styled as bordered boxes.
 *
 * Each box is a real <input>, so tapping a specific box puts the caret
 * there and the user can fix one digit without retyping the rest.
 * Auto-advances forward on type, jumps back + clears on backspace,
 * accepts a full-code paste on any box, and lets ArrowLeft/Right walk
 * between boxes for desktop testing.
 *
 * Only the first input carries `autoComplete="one-time-code"` since iOS
 * autofill targets the first field and writes the full value, which we
 * then split across the boxes via the paste handler.
 */
export default function OtpBoxes({
  value,
  onChange,
  length,
  error = false,
  autoFocus = false,
  ariaLabel,
}: OtpBoxesProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  // Focus the first empty slot when mounted (or fall back to slot 0).
  useEffect(() => {
    if (!autoFocus) return;
    const target = Math.min(value.length, length - 1);
    const id = window.setTimeout(
      () => refs.current[target]?.focus({ preventScroll: true }),
      80
    );
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Android/Chromium SMS autofill via the WebOTP API. iOS relies on the
  // `autocomplete="one-time-code"` hint on the inputs below; Android needs
  // this explicit request to read the code from a `@host #code` SMS and fill
  // it. Progressive enhancement: silently does nothing when unsupported or
  // when the SMS isn't WebOTP-formatted, so manual entry/paste still works.
  useEffect(() => {
    if (typeof window === "undefined" || !("OTPCredential" in window)) return;
    if (!navigator.credentials?.get) return;
    const ac = new AbortController();
    type OtpRequest = CredentialRequestOptions & {
      otp: { transport: string[] };
    };
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: ac.signal } as OtpRequest)
      .then((cred) => {
        const code = (cred as { code?: string } | null)?.code;
        if (code) onChange(code.replace(/\D/g, "").slice(0, length));
      })
      .catch(() => {
        // Aborted on unmount, or no SMS arrived — nothing to do.
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (index: number, digit: string) => {
    // Positional replace inside the compact value string. The user is
    // allowed to overwrite any already-typed digit by tapping its box,
    // and to append the next digit. Tapping a box past the next-empty
    // slot is a no-op so we never leave gaps (which would let malformed
    // strings reach the backend).
    if (digit) {
      if (index < value.length) {
        onChange(value.slice(0, index) + digit + value.slice(index + 1));
      } else if (index === value.length) {
        onChange(value + digit);
      }
    } else {
      // Empty digit = clear at this position. Truncate trailing digits
      // so the user keeps left-to-right ordering.
      onChange(value.slice(0, index));
    }
  };

  const handleChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (cleaned.length === 0) return;

    if (cleaned.length > 1) {
      // Two reasons we end up here with more than one digit:
      //  1. User typed in an already-filled box without focus's select()
      //     replacing — "8" was there, they typed "9", input value is
      //     "89". Treat as an overwrite: take the last digit.
      //  2. iOS SMS autofill drops the whole code into one box, or the
      //     user pasted. Fan the digits out across the remaining boxes.
      const isOverwrite =
        cleaned.length === 2 &&
        value[index] !== undefined &&
        cleaned[0] === value[index];

      if (isOverwrite) {
        setDigit(index, cleaned[1]);
        if (index < length - 1) refs.current[index + 1]?.focus();
        return;
      }

      // Fan out — paste / autofill path.
      const arr = value.split("");
      for (let i = 0; i < cleaned.length && index + i < length; i++) {
        arr[index + i] = cleaned[i];
      }
      const next = arr.join("").slice(0, length);
      onChange(next);
      const lastFilled = Math.min(index + cleaned.length, length) - 1;
      refs.current[lastFilled]?.focus();
      return;
    }

    setDigit(index, cleaned);
    if (index < length - 1) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const current = value[index] || "";
      if (current) {
        setDigit(index, "");
      } else if (index > 0) {
        setDigit(index - 1, "");
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.slice(0, length);
    onChange(next);
    const focusAt = Math.min(next.length, length - 1);
    refs.current[focusAt]?.focus();
  };

  return (
    <div className="flex gap-2" role="group" aria-label={ariaLabel}>
      {Array.from({ length }).map((_, i) => {
        const char = value[i] || "";
        const isFilled = !!char;
        const borderClass = error
          ? "border-red bg-red/[0.08]"
          : isFilled
          ? "border-orange-100/40"
          : "border-white/08 focus:border-orange-100";
        return (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="tel"
            inputMode="numeric"
            // NOT 1: SMS autofill (iOS one-time-code / Android Gboard) writes
            // the whole code into the focused box; a maxLength of 1 would clip
            // it to a single digit before handleChange can fan it out. The
            // controlled `value={char}` keeps each box showing one digit.
            maxLength={length}
            autoComplete="one-time-code"
            value={char}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            // Select content on focus so re-typing replaces the existing
            // digit cleanly instead of appending and being capped.
            onFocus={(e) => e.target.select()}
            className={`size-10 rounded-lg border-2 text-center text-base font-semibold text-white bg-transparent outline-none transition-colors ${borderClass}`}
          />
        );
      })}
    </div>
  );
}
